import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { parseFormattedNote } from '../utils/formatProgressNote';
import { formatPatientName } from '../utils/formatName';
import PatientARClaimsTable from '../components/PatientARClaimsTable';
import useCapabilities from '../hooks/useCapabilities';
import './AppointmentsPage.css';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';
import { openLedgerWith } from '../features/reports/ledgerLink';
import { openPatientOn } from '../utils/patientLink';

const API_URL = process.env.REACT_APP_API_URL || '';

// Claim ID, CPTs, ICD-10 and Note are hidden for now, for clients and staff alike
// (David, 2026-10-23): the coding is not ready to be read off the day view.
// Flipping this to true brings all four columns back; nothing else changes.
export const SHOW_CODING_COLUMNS = false;

const apptStatusColors = {
  Confirmed: '#2E9B6B',
  Scheduled: '#718096',
  Unconfirmed: '#D4AD5C',
  'Checked In': '#2D4470',
  'No Show': '#C53030',
  'No-show': '#C53030',
  'No-Show': '#C53030',
};

// `status_kind` is the backend's reading of the status word, the same one the weekly
// client report uses (digest_sources_service.status_kind), so the badge and the
// report never disagree about whether a visit happened. A no-show / cancelled /
// rescheduled visit gets the report's label; a kept visit keeps the feed's own word
// (Confirmed, Completed, the sheet's "completed"), coloured by the exact-word map
// above when it has an entry.
const statusKindLabels = {
  no_show: 'No-show',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};
const statusKindColors = {
  no_show: '#C53030',
  cancelled: '#718096',
  rescheduled: '#D4AD5C',
  kept: '#2E9B6B',
};

function statusBadge(appt) {
  const kind = appt.status_kind;
  const label = statusKindLabels[kind] || appt.status || 'Unknown';
  const color = apptStatusColors[appt.status] || statusKindColors[kind] || '#718096';
  return { label, color };
}

function todayInTimezone(timeZone) {
  // en-CA's date format is YYYY-MM-DD, which matches our wire format.
  // Falls back to local browser date when no TZ is supplied.
  const opts = timeZone ? { timeZone } : {};
  return new Date().toLocaleDateString('en-CA', opts);
}

function formatTime(isoString, timeZone) {
  if (!isoString) return '';
  try {
    const dt = new Date(isoString);
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (timeZone) opts.timeZone = timeZone;
    return dt.toLocaleTimeString('en-US', opts);
  } catch {
    return isoString;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function formatEligDate(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('T')[0].split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[parseInt(m, 10) - 1] + '. ' + parseInt(d, 10);
}

// 4-state eligibility icon — matches the daily email's visual language so
// both surfaces use the same algorithm + same colors. icon_state comes from
// classify_eligibility_check on the backend.
//   good      = green check (Active)
//   bad       = red X       (Rejected, Submission Error)
//   uncertain = orange ?    (Stale, No check on file, Unknown)
//   na        = gray dash   (No insurance / not applicable)
function EligIcon({ state }) {
  if (state === 'good') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#16a34a"/>
        <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (state === 'bad') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#dc2626"/>
        <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (state === 'na') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#6b7280"/>
        <path d="M7 12h10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }
  // uncertain — orange ?
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#d97706"/>
      <text x="12" y="17" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700" fontFamily="Arial, sans-serif">?</text>
    </svg>
  );
}

// Insurance on file: the server's flag, or the coverage it sent (a payer named or a
// member id) in case the two ever disagree again (Jordan, 2026-10-23: an unmapped Tebra
// payer read "No insurance" while the row carried the payer's name).
function hasInsurance(appt) {
  const c = appt.coverage;
  return Boolean(appt.has_insurance_info || (c && (c.payer_name || c.member_id)));
}

// The eligibility column as one icon (check, X, ? or dash), with the detail the cell
// used to spell out moved into the tooltip.
function eligSummary(appt) {
  const elig = appt.eligibility;
  if (appt.is_medicare && !elig) return { state: 'na', title: 'Medicare' };
  if (elig) {
    const parts = [elig.display_label || elig.status, elig.payer_name];
    if (elig.checked_at) parts.push(`checked ${formatEligDate(elig.checked_at)}`);
    return { state: elig.icon_state, title: parts.filter(Boolean).join(' · ') };
  }
  if (!appt.patient_id) return { state: 'uncertain', title: 'Not matched to a patient' };
  if (appt.skip_eligibility) return { state: 'na', title: 'Not billed to insurance' };
  // David 2026-10-23: a patient nobody has checked yet is a dash ("— Not checked");
  // the orange ? is reserved for a check that came back unknown.
  if (hasInsurance(appt)) return { state: 'na', title: 'No check on file' };
  return { state: 'na', title: 'No insurance on file' };
}

// The check's day as "Oct 21". Split the string rather than parse the ISO value
// so a late-evening UTC stamp does not slip to the previous day in a US browser.
// checked_at is a naive UTC timestamp (datetime.utcnow, no offset in the ISO string),
// so it is parsed as UTC and shown on the viewer's calendar day: a check run at 7pm
// Pacific on Oct 21 reads "Oct 21", not the UTC "Oct 22".
function formatEligShortDate(isoStr) {
  if (!isoStr) return '';
  const hasOffset = /(Z|[+-]\d\d:?\d\d)$/.test(isoStr);
  const date = new Date(hasOffset ? isoStr : `${isoStr}Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// The short word next to the icon. classify_eligibility_check's labels are the
// email's ("Active", "Submission Error: <detail>", "Stale (12d)"); the cell wants
// one or two words and leaves the detail to the tooltip.
function eligShortLabel(appt) {
  const elig = appt.eligibility;
  if (elig) {
    const label = elig.display_label || elig.status || '';
    if (elig.icon_state === 'good') return 'Eligible';
    if (/^submission error/i.test(label)) return 'Submission error';
    if (/^stale/i.test(label)) return 'Stale';
    return label || 'Unknown';
  }
  if (appt.is_medicare) return 'Medicare';
  if (!appt.patient_id) return 'Not matched';
  if (appt.skip_eligibility) return 'Not billed';
  if (hasInsurance(appt)) return 'Not checked';
  return 'No insurance';
}

function EligStatus({ appt }) {
  const { state, title } = eligSummary(appt);
  const date = appt.eligibility ? formatEligShortDate(appt.eligibility.checked_at) : '';
  const text = date ? `${eligShortLabel(appt)} \u00b7 ${date}` : eligShortLabel(appt);
  return (
    <span className="elig-icon-only" title={title} aria-label={title}>
      <EligIcon state={state} />
      <span className="elig-inline-text">{text}</span>
    </span>
  );
}

// Demo eligibility: the payer, what it said, and (only when it refused) why —
// short enough to scan down the column. Never the raw clearinghouse text.
function EligStack({ elig }) {
  // Outcome first, payer under it; the reason (if any) waits for a hover.
  return (
    <div className="elig-stack" title={elig.reason || undefined}>
      <span className={`elig-stack-outcome${elig.reason ? ' has-reason' : ''}`}>
        <EligIcon state={elig.outcome_icon} />
        {elig.outcome}
      </span>
      <span className="elig-stack-payer">{elig.payer_name || 'Unknown payer'}</span>
    </div>
  );
}

function NoteIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

// Green check on the note's upper-right corner once a claim exists for the visit.
function ClaimCheck() {
  return (
    <span className="claim-check" title="Claim created">
      {/* White ring so the check reads where it overlaps the note icon */}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10.5" fill="#16a34a" stroke="#fff" strokeWidth="3"/>
        <path d="M7.5 12.5l3 3 6-6" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

// `readOnly`: the client view. It changes the wording of the empty states only —
// a client is not the one who links a feed on the Data page — and is accepted and
// ignored by everything else on the page.
function AppointmentsPage({ selectedPractice, readOnly = false }) {
  const [selectedDate, setSelectedDate] = useState(() =>
    todayInTimezone(selectedPractice?.timezone)
  );
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastSyncRange, setLastSyncRange] = useState(null);
  // `enabled`: a sync is available (Tebra, PracticeBetter, Cobalt, the demo). It gates
  // the two toolbar buttons and nothing else. `hasAppointments`: the appointments
  // table holds rows for this practice from ANY feed — the sheet, Cobalt, a Kinnser
  // visits export. false is the "nothing linked yet" state. null = not yet answered.
  const [enabled, setEnabled] = useState(null);
  const [hasAppointments, setHasAppointments] = useState(null);
  const [activeTypeFilters, setActiveTypeFilters] = useState(new Set()); // empty = show all
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [noteModal, setNoteModal] = useState(null); // { patientName, note } or null
  // { patientName, claims (null while loading), error } or null
  const [balanceModal, setBalanceModal] = useState(null);

  const typeDropdownRef = useRef(null);
  const practiceId = selectedPractice?.id;
  // The demo practice (emr_system = "demo"): fake people and seeded checks.
  // `is_demo` now means only "this data is fake" — it is NOT a stand-in for what
  // a practice's feed can do. Anything that depends on the feed reads the gate
  // below instead; anything that depends on the data being fake (the banner, the
  // "Regenerate" label, refusing to spend money on a live eligibility check)
  // still reads this.
  const isDemo = Boolean(selectedPractice?.is_demo);
  const [demoNoticeOpen, setDemoNoticeOpen] = useState(true);
  // Home health (practices.service_model, deprecated; the Waterfall no longer reads it): the rows are
  // Kinnser visits — discipline in `visit_kind`, the visit type in
  // `appointment_type`, the clinician in `provider_name` — and no CPT or ICD hangs
  // off a visit, so those columns are swapped for the visit's own.
  const isHomeHealth = selectedPractice?.service_model === 'home_health';

  // What this practice's feeds supply. Used here for ONE thing: adding the
  // Patient Balance column, which was previously shown to the demo practice
  // alone because `is_demo` was the only condition available when it was built.
  //
  // NOTHING on this page is REMOVED on a capability, and that is deliberate.
  // A practice can bill from one system and keep its diary in another, so no
  // single capability answers for a whole page. Vortex is the case that proves
  // it: its claims arrive as a PracticeSuite CSV, so its SOURCE is
  // `csv_practicesuite` — {charges, statuses} — while its appointments come from
  // Tebra, which `connector_registry.scheduling_capabilities` reports separately.
  // So `features.roster` is true for Vortex and it gains the Patient Balance
  // column, which is the right answer.
  //
  // But the Tebra sync writes Appointment rows and no payer, member id or
  // subscriber, so Vortex has NO `coverages` and `features.eligibility` and
  // `features.copay` are both false — correctly, because nothing syncs its
  // coverages. Gating the Eligibility or Copay columns, or the Check Eligibility
  // button, on those would therefore still strip surfaces its front desk uses
  // every day. Hence: capabilities add here, they never subtract.
  //
  // `loading` and `error` matter for the same reason: a fetch that has not
  // answered, or has failed, is not a statement about a practice's feeds. In
  // both windows this falls back to the pre-gate behaviour.
  const { features, loading: capsLoading, error: capsError } = useCapabilities(practiceId);
  const capsKnown = !capsLoading && !capsError;
  const showRosterColumns = capsKnown ? Boolean(features.roster) : isDemo;

  const appointmentTypes = useMemo(() => {
    const counts = {};
    appointments.forEach((a) => {
      const t = a.appointment_type || 'Unknown';
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [appointments]);

  const filteredAppointments = useMemo(() => {
    if (activeTypeFilters.size === 0) return appointments;
    return appointments.filter((a) => activeTypeFilters.has(a.appointment_type || 'Unknown'));
  }, [appointments, activeTypeFilters]);

  const toggleTypeFilter = useCallback((type) => {
    setActiveTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const loadAppointments = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/appointments?practice_id=${practiceId}&date=${selectedDate}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setEnabled(data.enabled !== false);
        setHasAppointments(data.has_appointments !== false);
        setAppointments(data.appointments || []);
      }
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, selectedDate]);

  // Reset state when practice changes — including the date picker, since
  // "today" depends on the practice's timezone (PT vs CT vs ET).
  useEffect(() => {
    setEnabled(null);
    setHasAppointments(null);
    setAppointments([]);
    setLastSyncRange(null);
    setMessage(null);
    setActiveTypeFilters(new Set());
    setSelectedDate(todayInTimezone(selectedPractice?.timezone));
  }, [practiceId, selectedPractice?.timezone]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Close type dropdown on outside click
  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleClick = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [typeDropdownOpen]);

  const handleSync = async () => {
    if (!practiceId) return;
    setSyncing(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/appointments/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ practice_id: practiceId, date: selectedDate }),
      });
      const data = await response.json();
      if (response.ok && data.success && data.demo) {
        setMessage({ type: 'success', text: data.message || 'Demo schedule regenerated' });
        loadAppointments();
      } else if (response.ok && data.success) {
        setMessage({
          type: 'success',
          text: `Synced ${data.synced} appointments (${data.matched} matched to patients). Range: ${formatDate(data.range_start)} \u2013 ${formatDate(data.range_end)}`,
        });
        setLastSyncRange({ start: data.range_start, end: data.range_end });
        loadAppointments();
      } else {
        const debugInfo = data.debug_error_type ? ` [${data.debug_error_type}: ${data.debug_error_msg}]` : '';
        setMessage({ type: 'error', text: (data.error || 'Sync failed') + debugInfo });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    } finally {
      setSyncing(false);
    }
  };

  // The visit's claim on the Claims tab: this patient (by id when matched, else by
  // name) on this date of service.
  const openClaims = (appt) => {
    const day = appt.appointment_date || selectedDate;
    openLedgerWith(practiceId, {
      patient_id: appt.patient_id ? String(appt.patient_id) : '',
      patient: appt.patient_name || '',
      date_from: day,
      date_to: day,
    });
  };

  // The patient's outstanding claims, from the same endpoint the A/R tab uses.
  const openBalance = async (appt) => {
    setBalanceModal({ patientName: appt.patient_name, claims: null, error: null });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/ar/patients/claims?patient_id=${appt.patient_id}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to load claims');
      const data = await response.json();
      setBalanceModal((m) => (m ? { ...m, claims: data.data || [] } : m));
    } catch (err) {
      setBalanceModal((m) => (m ? { ...m, error: err.message || 'Failed to load claims' } : m));
    }
  };

  const changeDate = (delta) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  if (!selectedPractice) {
    return (
      <div className="appointments-page">
        <div className="empty-state">
          <h3>Select a practice</h3>
          <p>Choose a practice from the sidebar to view appointments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <h1 className="page-title">Appointments</h1>
        {/* The sync and the paid eligibility check exist only where a sync does; a
            sheet or CSV practice reads its rows and has nothing to press. */}
        {enabled && !readOnly && (
        <div className="appointments-header-actions">
          <button
            className="btn-sync"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <span className="btn-spinner" />
                Syncing...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                {isDemo ? 'Regenerate demo schedule' : 'Sync with EMR'}
              </>
            )}
          </button>
        </div>
        )}
      </div>

      {isDemo && demoNoticeOpen && (
        <div className="appt-message info">
          <span>
            <strong>Demo practice.</strong> Every patient here is fictional; eligibility, copays and balances are seeded, and nothing is sent to a payer.
          </span>
          <button className="message-close" onClick={() => setDemoNoticeOpen(false)}>&times;</button>
        </div>
      )}

      {message && (
        <div className={`appt-message ${message.type}`}>
          <span>{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      <div className="date-nav">
        <button className="date-nav-btn" onClick={() => changeDate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </button>
        <input
          type="date"
          className="date-input"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button className="date-nav-btn" onClick={() => changeDate(1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,6 15,12 9,18" />
          </svg>
        </button>
        <button className="date-today-btn" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>
          Today
        </button>
        {lastSyncRange && (
          <span className="sync-range-label">
            Synced: {formatDate(lastSyncRange.start)} &ndash; {formatDate(lastSyncRange.end)}
          </span>
        )}
      </div>

      {!loading && appointments.length > 0 && appointmentTypes.length > 1 && (
        <div className="type-filter-bar">
          <div className="type-filter-dropdown-wrap" ref={typeDropdownRef}>
            <button
              className="type-filter-dropdown-btn"
              onClick={() => setTypeDropdownOpen((prev) => !prev)}
            >
              {activeTypeFilters.size === 0
                ? 'All Types'
                : `${activeTypeFilters.size} Type${activeTypeFilters.size !== 1 ? 's' : ''}`}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
            {typeDropdownOpen && (
              <div className="type-filter-dropdown-menu">
                {appointmentTypes.map(([type, count]) => (
                  <label key={type} className="type-filter-option">
                    <input
                      type="checkbox"
                      checked={activeTypeFilters.has(type)}
                      onChange={() => toggleTypeFilter(type)}
                    />
                    <span className="type-filter-option-label">{type}</span>
                    <span className="type-filter-option-count">{count}</span>
                  </label>
                ))}
                {activeTypeFilters.size > 0 && (
                  <button
                    className="type-filter-clear-btn"
                    onClick={() => { setActiveTypeFilters(new Set()); setTypeDropdownOpen(false); }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
          {activeTypeFilters.size > 0 && (
            <span className="type-filter-summary">
              {filteredAppointments.length} of {appointments.length}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading appointments...</p>
        </div>
      ) : hasAppointments === false ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {readOnly ? (
            <>
              <h3>No appointments yet</h3>
              <p>Appointments appear here once this practice's schedule is connected.</p>
            </>
          ) : (
            <>
              <h3>No appointments yet — link a feed on the Data page</h3>
              <p>
                Atlas holds no appointments for <strong>{selectedPractice.name}</strong> from any feed.
                {enabled && (isDemo
                  ? ' Or click "Regenerate demo schedule" to build one.'
                  : ' Or click "Sync with EMR" to pull the schedule.')}
              </p>
            </>
          )}
        </div>
      ) : appointments.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h3>No appointments</h3>
          <p>
            No appointments found for {formatDate(selectedDate)}.
            {!readOnly && enabled && (isDemo
              ? ' Click "Regenerate demo schedule" to rebuild it around today.'
              : ' Click "Sync with EMR" to pull the latest schedule.')}
          </p>
        </div>
      ) : (
        <div className="appointments-table-wrap">
          <table
            className={[
              'appointments-table',
              isDemo ? 'appointments-table-demo' : '',
              // The 28% patient width was set for a table without the front-desk
              // columns; with Provider and Patient Balance added it pushes the
              // right-hand columns into the wrapper's horizontal scroll.
              showRosterColumns ? 'appointments-table-roster' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <thead>
              <tr>
                <th>Time</th>
                <th>Patient</th>
                {!isHomeHealth && <th>Provider</th>}
                {isHomeHealth ? (
                  <>
                    <th>Discipline</th>
                    <th>Visit</th>
                    <th>Clinician</th>
                  </>
                ) : (
                  <th>Type</th>
                )}
                {SHOW_CODING_COLUMNS && <th>Claim ID</th>}
                {SHOW_CODING_COLUMNS && isDemo && <th>Note</th>}
                {SHOW_CODING_COLUMNS && !isDemo && !isHomeHealth && <th>CPTs</th>}
                {SHOW_CODING_COLUMNS && !isDemo && !isHomeHealth && <th>ICD-10</th>}
                {!isDemo && <th>Status</th>}
                <th>Eligibility</th>
                {showRosterColumns && (
                  <th title="Patient-owed remainder on earlier visits">Patient Balance</th>
                )}
                {SHOW_CODING_COLUMNS && !isDemo && <th>Note</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((appt) => {
                const badge = statusBadge(appt);
                return (
                  <tr key={appt.id} className="appt-row">
                    <td className="appt-time">{formatTime(appt.start_time, appt.timezone)}</td>
                    <td className="appt-patient">
                      <div className="appt-patient-content">
                        {appt.patient_id ? (
                          <button
                            className="balance-link patient-name"
                            onClick={() => openPatientOn('patients', practiceId, appt.patient_id)}
                            title="Open this patient's profile"
                          >
                            {formatPatientName(appt.patient_name)}
                          </button>
                        ) : (
                          <span className="patient-name">{formatPatientName(appt.patient_name)}</span>
                        )}
                      </div>
                    </td>
                    {!isHomeHealth && <td className="appt-provider">{appt.provider_name || '—'}</td>}
                    {isHomeHealth ? (
                      <>
                        <td className="appt-type">{appt.visit_kind || '\u2014'}</td>
                        <td className="appt-type">{appt.appointment_type || '\u2014'}</td>
                        <td className="appt-provider">{appt.provider_name || '\u2014'}</td>
                      </>
                    ) : (
                      <td className="appt-type">{appt.appointment_type || '\u2014'}</td>
                    )}
                    {SHOW_CODING_COLUMNS && (
                    <td className="appt-claim">
                      {appt.claim_id ? (
                        <button
                          className="balance-link"
                          onClick={() => openClaims(appt)}
                          title="Show this visit's claim on the Claims tab"
                        >
                          {appt.claim_control_number || 'View claim'}
                        </button>
                      ) : (
                        <span className="copay-empty">{'\u2014'}</span>
                      )}
                    </td>
                    )}
                    {SHOW_CODING_COLUMNS && isDemo && (
                      <td className="appt-note-steps">
                        <span className="note-steps">
                          {appt.notes ? (
                            <button
                              className="note-icon-btn"
                              onClick={() => setNoteModal({ patientName: appt.patient_name, note: appt.notes })}
                              title="View progress note"
                            >
                              <NoteIcon size={20} />
                            </button>
                          ) : (
                            <span className="note-icon-muted" title="No note yet">
                              <NoteIcon size={20} />
                            </span>
                          )}
                          {appt.notes && appt.claim_created && <ClaimCheck />}
                        </span>
                      </td>
                    )}
                    {SHOW_CODING_COLUMNS && !isDemo && !isHomeHealth && (
                      <>
                        <td className="appt-cpts">
                          {appt.cpt_codes ? (
                            <span className="appt-cpt-value">{appt.cpt_codes}</span>
                          ) : (
                            <span className="appt-cpt-empty">{'\u2014'}</span>
                          )}
                        </td>
                        <td className="appt-icd">
                          {appt.icd_codes ? (
                            <span className="appt-icd-value">{appt.icd_codes}</span>
                          ) : (
                            <span className="appt-icd-empty">{'\u2014'}</span>
                          )}
                        </td>
                      </>
                    )}
                    {!isDemo && (
                      <td>
                        <span className="status-badge" style={{ background: badge.color }}>
                          {badge.label}
                        </span>
                      </td>
                    )}
                    <td>
                      {isDemo && appt.eligibility ? (
                        <EligStack elig={appt.eligibility} />
                      ) : (
                        <EligStatus appt={appt} />
                      )}
                    </td>
                    {showRosterColumns && (
                      <td className="appt-balance">
                        {appt.outstanding_balance != null ? (
                          <button
                            className="balance-link"
                            onClick={() => openBalance(appt)}
                            title="View outstanding claims"
                          >
                            ${Number(appt.outstanding_balance).toFixed(2)}
                          </button>
                        ) : (
                          <span className="copay-empty">{'—'}</span>
                        )}
                      </td>
                    )}
                    {SHOW_CODING_COLUMNS && !isDemo && (
                    <td className="appt-note">
                      {appt.notes ? (
                        <button
                          className="note-icon-btn"
                          onClick={() => setNoteModal({ patientName: appt.patient_name, note: appt.notes })}
                          title="View progress note"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                          </svg>
                        </button>
                      ) : (
                        <span className="appt-note-empty">{'\u2014'}</span>
                      )}
                    </td>
                    )}
                    <td className="appt-actions">
                      {appt.patient_id && (
                        <button
                          className="btn btn-sm"
                          onClick={() => openPatientOn('eligibility', practiceId, appt.patient_id, {
                            // The visit's coverage on file, so the check form opens filled
                            // (payer, member id, DOB, provider, this date). Read once there.
                            coverage: appt.coverage || null,
                            dob: appt.dob || null,
                            provider_name: appt.provider_name || null,
                            appointment_date: appt.appointment_date || null,
                          })}
                        >
                          Check Eligibility
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="appointments-summary">
            {filteredAppointments.length === appointments.length
              ? `${appointments.length} appointment${appointments.length !== 1 ? 's' : ''}`
              : `${filteredAppointments.length} of ${appointments.length} appointments`}
          </div>
        </div>
      )}

      {noteModal && (
        <div className="modal-overlay" onClick={() => setNoteModal(null)}>
          <div className="modal-content note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Progress Note — {noteModal.patientName}</h3>
              <button className="close-btn" onClick={() => setNoteModal(null)}>&times;</button>
            </div>
            <div className="note-modal-body">
              {(() => {
                const parsed = parseFormattedNote(noteModal.note)
                  .filter((s) => s.type !== 'intro' && (s.label || s.body));
                const header = parsed.filter((s) => s.zone === 'header');
                const body = parsed.filter((s) => s.zone === 'body');
                const footer = parsed.filter((s) => s.zone === 'footer');

                const renderSection = (section, idx) => (
                  <div key={idx} className="note-section">
                    <span className="note-section-label">{section.label}:</span>{' '}
                    <span className="note-section-body">{section.body}</span>
                  </div>
                );

                return (
                  <>
                    {header.length > 0 && (
                      <div className="note-zone note-zone-header">
                        {header.map(renderSection)}
                      </div>
                    )}
                    {body.length > 0 && (
                      <div className="note-zone note-zone-body">
                        {body.map(renderSection)}
                      </div>
                    )}
                    {footer.length > 0 && (
                      <div className="note-zone note-zone-footer">
                        {footer.map(renderSection)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {balanceModal && (
        <div className="modal-overlay" onClick={() => setBalanceModal(null)}>
          <div className="modal-content note-modal balance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Outstanding Claims — {formatPatientName(balanceModal.patientName)}</h3>
              <button className="close-btn" onClick={() => setBalanceModal(null)}>&times;</button>
            </div>
            <div className="balance-modal-body">
              {balanceModal.error ? (
                <p className="balance-modal-error">{balanceModal.error}</p>
              ) : balanceModal.claims === null ? (
                <p className="balance-modal-status">Loading claims...</p>
              ) : balanceModal.claims.length === 0 ? (
                <p className="balance-modal-status">No outstanding claims.</p>
              ) : (
                <PatientARClaimsTable claims={balanceModal.claims} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentsPage;
